// src/components/admin/Sidebar.js
import React, { useContext, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Avatar,
  Stack,
  GlobalStyles,
  IconButton,
  Divider,
} from "@mui/material";

import PeopleIcon from "@mui/icons-material/PeopleOutline";
import PersonAddIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import DomainIcon from "@mui/icons-material/DomainOutlined";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import NoteAddIcon from "@mui/icons-material/NoteAddOutlined";
import Inventory2Icon from "@mui/icons-material/Inventory2Outlined";
import AddBoxIcon from "@mui/icons-material/AddBoxOutlined";
import LocalShippingIcon from "@mui/icons-material/LocalShippingOutlined";
import MapIcon from "@mui/icons-material/MapOutlined";
import PublicIcon from "@mui/icons-material/PublicOutlined";
import BusinessIcon from "@mui/icons-material/BusinessOutlined";
import AccountTreeIcon from "@mui/icons-material/AccountTreeOutlined";
import DriverIcon from "@mui/icons-material/BadgeOutlined";
import StorefrontIcon from "@mui/icons-material/StorefrontOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import LogoutIcon from "@mui/icons-material/LogoutOutlined";

import { useNavigate, useLocation } from "react-router-dom";
import { Context } from "../../index";
import api from "../../http";

import {
  CreateUserPagePageRoute,
  UserListPageRoute,
  CreateProductPagePageRoute,
  ProductListPageRoute,
  DeliveryAreasComponentPageRoute,
  TransportListPageRoute,
  CreateTransportPageRoute,
  AdminClientsListPageRoute,
  ContractsListAdminPageRoute,
  CreateContractAdminPageRoute,
  OrganizationsListPageRoute,
  DivisionsListPageRoute,
  RegionsListPageRoute,
  DriversListPageRoute,
} from "../../consts";

const DRAWER_WIDTH = 248;
const SURFACE       = "#FFFFFF";
const SURFACE_SOFT  = "#FAF7F2";
const BORDER        = "rgba(56, 47, 42, 0.08)";
const TEXT          = "#382F2A";
const TEXT_MUTED    = "#605148";
const TEXT_DIM      = "#8C8077";
const BRAND         = "#F08F5A";
const BRAND_DEEP    = "#E87537";
const ACTIVE_BG     = "rgba(240, 143, 90, 0.10)";

// --- helper: активный пункт ---
const useIsActive = () => {
  const location = useLocation();
  return (pathPrefix) =>
    location.pathname.toLowerCase().startsWith(pathPrefix.toLowerCase());
};

const NAV_GROUPS = [
  {
    title: "Пользователи",
    items: [
      { label: "Пользователи",          path: UserListPageRoute,          Icon: PeopleIcon },
      { label: "Создать пользователя",  path: CreateUserPagePageRoute,    Icon: PersonAddIcon },
    ],
  },
  {
    title: "Клиенты и договоры",
    items: [
      { label: "Клиенты",          path: AdminClientsListPageRoute,    Icon: DomainIcon },
      { label: "Список договоров", path: ContractsListAdminPageRoute,  Icon: DescriptionIcon },
      { label: "Создать договор",  path: CreateContractAdminPageRoute, Icon: NoteAddIcon },
    ],
  },
  {
    title: "Товары",
    items: [
      { label: "Список товаров", path: ProductListPageRoute,        Icon: Inventory2Icon },
      { label: "Добавить товар", path: CreateProductPagePageRoute,  Icon: AddBoxIcon },
    ],
  },
  {
    title: "Транспорт",
    items: [
      { label: "Транспорт",          path: TransportListPageRoute,    Icon: LocalShippingIcon },
      { label: "Добавить транспорт", path: CreateTransportPageRoute,  Icon: AddBoxIcon },
      { label: "Водители",           path: DriversListPageRoute,      Icon: DriverIcon },
    ],
  },
  {
    title: "Справочники",
    items: [
      { label: "Организации",      path: OrganizationsListPageRoute,      Icon: BusinessIcon },
      { label: "Подразделения",    path: DivisionsListPageRoute,          Icon: AccountTreeIcon },
      { label: "Регионы",          path: RegionsListPageRoute,            Icon: PublicIcon },
      { label: "Регионы доставки", path: DeliveryAreasComponentPageRoute, Icon: MapIcon },
    ],
  },
];

const navItemSx = (active) => ({
  position: 'relative',
  borderRadius: 8,
  mx: 1,
  my: 0.25,
  px: 1.25,
  py: 0.875,
  color: active ? BRAND_DEEP : TEXT_MUTED,
  bgcolor: active ? ACTIVE_BG : 'transparent',
  fontWeight: active ? 600 : 500,
  transition: 'background 160ms ease, color 160ms ease',
  '&:hover': { bgcolor: active ? ACTIVE_BG : 'rgba(56, 47, 42, 0.04)', color: TEXT },
  '& .MuiSvgIcon-root': { fontSize: 18, mr: 1.25, color: 'currentColor' },
  ...(active && {
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 8,
      bottom: 8,
      width: 3,
      borderRadius: 3,
      background: BRAND,
    },
  }),
});

const subheaderSx = {
  bgcolor: 'transparent',
  color: TEXT_DIM,
  textTransform: 'uppercase',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.08em',
  lineHeight: 1.2,
  px: 2.25,
  py: 1.25,
};

const SidebarContent = observer(function SidebarContent({ onItemClick }) {
  const { user } = useContext(Context);
  const navigate = useNavigate();
  const isActive = useIsActive();

  const go = (path) => {
    navigate(path);
    onItemClick?.();
  };

  const goToShop = () => { navigate('/Applications'); onItemClick?.(); };

  const handleLogout = async () => {
    try { await api.post('v1/logout', {}); } catch { /* no-op */ }
    user.logout();
    navigate('/');
  };

  const fullName = [user?.profile?.lastname, user?.profile?.firstname].filter(Boolean).join(' ')
                || user?.user?.login || '—';
  const role = { 1: 'Администратор', 2: 'Оператор', 3: 'Пользователь' }[Number(user?.role)] || '—';
  const initials = fullName.trim().split(/\s+/).slice(0, 2).map(s => s[0]?.toUpperCase()).join('') || 'A';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Brand mark */}
      <Box sx={{ px: 2.25, pt: 2.5, pb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box sx={{
            width: 32, height: 32, borderRadius: 2,
            background: `linear-gradient(135deg, ${BRAND}, ${BRAND_DEEP})`,
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: 13, letterSpacing: '0.04em',
          }}>MB</Box>
          <Box sx={{ lineHeight: 1.15 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: TEXT, lineHeight: 1.1 }}>
              MB4 Console
            </Typography>
            <Typography sx={{ fontSize: 11, color: TEXT_DIM, lineHeight: 1.2 }}>
              Админ-панель
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Divider sx={{ borderColor: BORDER, mx: 2 }} />

      {/* Search-плашка (для красоты, без логики) */}
      <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          height: 34, px: 1.25, borderRadius: 8,
          bgcolor: 'rgba(56, 47, 42, 0.04)',
          color: TEXT_DIM, fontSize: 13,
        }}>
          <SearchIcon sx={{ fontSize: 16 }} />
          <span>Поиск разделов…</span>
          <Box sx={{
            ml: 'auto', fontSize: 10, fontWeight: 600,
            px: 0.75, py: 0.25, borderRadius: 1,
            bgcolor: 'rgba(56, 47, 42, 0.08)', color: TEXT_DIM,
          }}>⌘K</Box>
        </Box>
      </Box>

      {/* Nav groups */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {NAV_GROUPS.map((group) => (
          <List
            key={group.title}
            dense
            disablePadding
            subheader={
              <ListSubheader disableSticky sx={subheaderSx}>{group.title}</ListSubheader>
            }
          >
            {group.items.map(({ label, path, Icon }) => {
              const active = isActive(path);
              return (
                <ListItemButton
                  key={path}
                  selected={active}
                  onClick={() => go(path)}
                  sx={navItemSx(active)}
                >
                  <Icon />
                  <ListItemText
                    primary={label}
                    primaryTypographyProps={{
                      fontSize: 13.5,
                      fontWeight: 'inherit',
                      lineHeight: 1.3,
                      color: 'inherit',
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        ))}
      </Box>

      {/* Profile footer */}
      <Box sx={{ borderTop: `1px solid ${BORDER}`, p: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 1, py: 0.5 }}>
          <Avatar sx={{
            width: 32, height: 32, bgcolor: BRAND_DEEP,
            fontSize: 12, fontWeight: 700,
          }}>{initials}</Avatar>
          <Box sx={{ flex: 1, minWidth: 0, lineHeight: 1.15 }}>
            <Typography sx={{
              fontSize: 13, fontWeight: 600, color: TEXT,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{fullName}</Typography>
            <Typography sx={{ fontSize: 11, color: TEXT_DIM }}>{role}</Typography>
          </Box>
          <IconButton
            size="small"
            onClick={handleLogout}
            sx={{ color: TEXT_DIM, '&:hover': { color: '#B53A36', bgcolor: 'rgba(235,87,87,0.08)' } }}
            aria-label="Выйти"
            title="Выйти"
          >
            <LogoutIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>

        <ListItemButton
          onClick={goToShop}
          sx={{
            mt: 0.5,
            borderRadius: 8,
            px: 1.25,
            py: 0.75,
            color: BRAND_DEEP,
            fontWeight: 600,
            fontSize: 13,
            justifyContent: 'flex-start',
            '&:hover': { bgcolor: 'rgba(240, 143, 90, 0.08)' },
            '& .MuiSvgIcon-root': { fontSize: 16, mr: 1 },
          }}
        >
          <StorefrontIcon />
          Вернуться в&nbsp;CRM
        </ListItemButton>
      </Box>
    </Box>
  );
});

function Sidebar() {
  return (
    <Drawer
      variant="permanent"
      sx={{
        display: { xs: 'none', md: 'block' },
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          bgcolor: SURFACE,
          color: TEXT,
          borderRight: `1px solid ${BORDER}`,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <SidebarContent />
    </Drawer>
  );
}

export const AdminShell = observer(function AdminShell({ title, subtitle, actions, breadcrumbs, children }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100dvh',
        bgcolor: SURFACE_SOFT,
        color: TEXT,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", system-ui, Roboto, "Segoe UI", Arial, sans-serif',
      }}
    >
      <GlobalStyles styles={{
        'html, body, #root': { height: '100%', overflow: 'hidden' },
      }} />

      <Sidebar />

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            bgcolor: SURFACE,
            color: TEXT,
            borderRight: 'none',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <SidebarContent onItemClick={() => setMobileOpen(false)} />
      </Drawer>

      {/* Main */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden',
        }}
      >
        {/* Top app-bar */}
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            left: { xs: 0, md: `${DRAWER_WIDTH}px` },
            right: 0,
            width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
            bgcolor: 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(10px)',
            color: TEXT,
            borderBottom: `1px solid ${BORDER}`,
          }}
        >
          <Toolbar sx={{
            minHeight: 60,
            pl: { xs: 1.5, md: 3 },
            pr: { xs: 1.5, md: 3 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
              <IconButton
                edge="start"
                onClick={() => setMobileOpen(true)}
                sx={{ display: { xs: 'inline-flex', md: 'none' }, color: TEXT }}
                aria-label="Открыть меню"
              ><MenuIcon /></IconButton>

              <Box sx={{ minWidth: 0 }}>
                {breadcrumbs && (
                  <Typography sx={{
                    fontSize: 11, color: TEXT_DIM, fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: '0.04em',
                    lineHeight: 1.2,
                  }} noWrap>{breadcrumbs}</Typography>
                )}
                <Typography sx={{
                  fontSize: 17, fontWeight: 700, color: TEXT,
                  lineHeight: 1.25, letterSpacing: '-0.01em',
                }} noWrap>{title || '—'}</Typography>
                {subtitle && (
                  <Typography sx={{ fontSize: 12, color: TEXT_MUTED, lineHeight: 1.3 }} noWrap>
                    {subtitle}
                  </Typography>
                )}
              </Box>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1}>
              {actions}
            </Stack>
          </Toolbar>
        </AppBar>

        {/* Content */}
        <Box sx={{
          flex: 1,
          pt: { xs: 8, md: 8.5 },
          px: { xs: 1.5, md: 3 },
          pb: 3,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}>
          <Box sx={{
            flex: 1,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            minHeight: 0,
          }}>
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
});

export default Sidebar;
